/**
 * The Ctrl+K palette.
 *
 * PRESENTATIONAL. It is handed a list of commands and reports which one was
 * chosen; it does not know what a route is, does not read the database, and does
 * not import the data layer. That is deliberate, and it is what makes the
 * palette work at all: it is the one navigation surface available before any
 * import has happened, and the one that must keep working if the data layer
 * fails.
 *
 * Two behaviours are worth naming:
 *  - The input keeps focus while the results change, so typing does not stop
 *    after the first keystroke that filters.
 *  - Enter picks the highlighted row, and the highlight WRAPS, so holding Enter
 *    cycles rather than sticking on the last item.
 */
import { useEffect, useRef, useState } from 'react';
import { CornerDownLeft, Search } from 'lucide-react';
import { nextSelection, searchCommands, type Command } from '../lib/commandPalette';
import { cn } from '../lib/cn';

export interface CommandPaletteProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly commands: readonly Command[];
  /** Called with the chosen command. Navigation is the caller's business. */
  readonly onChoose: (command: Command) => void;
}

export function CommandPalette({ open, onClose, commands, onChoose }: CommandPaletteProps) {
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  const results = searchCommands(commands, query);

  // Reset per opening, so last time's search is not waiting in the box.
  useEffect(() => {
    if (!open) return;
    setQuery('');
    setSelected(0);
    // Focus after paint, or the browser drops it on the next render.
    const id = window.setTimeout(() => inputRef.current?.focus(), 0);
    return () => window.clearTimeout(id);
  }, [open]);

  // A shorter result list must not leave the highlight pointing past the end.
  useEffect(() => {
    setSelected((s) => (s >= results.length ? 0 : s));
  }, [results.length]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  function choose(command: Command | undefined) {
    if (!command) return;
    onChoose(command);
    onClose();
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/60 p-4 pt-[10vh]"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Command palette"
        data-testid="command-palette"
        className="w-full max-w-lg overflow-hidden rounded-lg border border-slate-700 bg-slate-900 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 border-b border-slate-800 px-3">
          <Search className="h-4 w-4 shrink-0 text-slate-500" aria-hidden />
          <input
            ref={inputRef}
            type="text"
            role="combobox"
            aria-expanded="true"
            aria-controls="command-palette-results"
            aria-autocomplete="list"
            aria-label="Search screens and actions"
            placeholder="Jump to…"
            className="w-full bg-transparent py-3 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setSelected(0);
            }}
            onKeyDown={(e) => {
              // Bound to a local so TypeScript can narrow it. `e.key` is a
              // property access, and narrowing does not survive into the
              // `setSelected` callback where the value is re-read.
              const key = e.key;
              if (key === 'ArrowDown' || key === 'ArrowUp' || key === 'Home' || key === 'End') {
                e.preventDefault();
                setSelected((s) => nextSelection(s, key, results.length));
              } else if (key === 'Enter') {
                e.preventDefault();
                choose(results[selected]);
              }
            }}
          />
        </div>

        <ul
          id="command-palette-results"
          role="listbox"
          aria-label="Results"
          className="max-h-80 overflow-y-auto p-1"
        >
          {results.length === 0 ? (
            <li className="px-3 py-6 text-center text-sm text-slate-500">Nothing matches that.</li>
          ) : (
            results.map((command, index) => (
              <li key={command.id}>
                <button
                  type="button"
                  role="option"
                  aria-selected={index === selected}
                  // `onMouseDown` fires before the input's blur, so the click is
                  // not lost to a focus change mid-press.
                  onMouseDown={(e) => {
                    e.preventDefault();
                    choose(command);
                  }}
                  onMouseEnter={() => setSelected(index)}
                  className={cn(
                    'flex w-full items-center justify-between gap-3 rounded px-3 py-2 text-left text-sm',
                    index === selected ? 'bg-slate-800 text-white' : 'text-slate-300 hover:bg-slate-800/60',
                  )}
                >
                  <span className="truncate">{command.label}</span>
                  <span className="flex shrink-0 items-center gap-2 text-xs text-slate-500">
                    {command.group}
                    {index === selected ? <CornerDownLeft className="h-3.5 w-3.5" aria-hidden /> : null}
                  </span>
                </button>
              </li>
            ))
          )}
        </ul>

        <p className="border-t border-slate-800 px-3 py-2 text-xs text-slate-500">
          Up and down to move · Enter to open · Esc to close
        </p>
      </div>
    </div>
  );
}

export type { Command };
